import { FC, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'motion/react';

import { RightNavProps } from 'components/home/TopNavigation/top-navigation.types';
import {
  SiteHeaderCtas,
  NavDropdownContent,
  SiteHeader,
  listVariants,
  tabVariants,
  TabButton,
  CartCount,
  Referral25Label,
  TabPill,
  lightPill,
} from 'components/home/TopNavigation/top-navigation.styled';

import dynamic from 'next/dynamic';
import Button from 'components/library/Button';
import { UserContext } from 'context-providers/user-provider';
import { useRouter } from 'next/router';
import { getAccountDropdownContent } from '../account-dropdown';
import { useIsOneTimePurchaser } from 'hooks/use-is-one-time-purchaser';
import NavSubLists from '../nav-sub-lists';
import { CartContext } from 'context-providers/cart-provider';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import { getNavCtas, getNavState } from 'lib/user/get-nav-state';
import { trackEvent } from 'lib/analytics';
import { useFeatureFlags } from 'context-providers/feature-flag-provider';
import LoginDropdownPercentRollout from '../login-dropdown-percent-rollout';
import {
  useIsJourneyMember,
  useJourneyDestination,
} from 'context-providers/journey-provider';

const LoginDropdown = dynamic(
  () => import('components/home/TopNavigation/login-dropdown'),
);

const LoginButton = styled.button`
  background: none;
  border: none;
  color: var(--fill-primary);
  font-size: var(--font-size-350);
  cursor: pointer;
  padding: 0.5em 1em;

  html:not(.with-little-scrolled) &.theme-green {
    color: var(--nav-ink, var(--fill-brand));
  }
`;

const CartButton = styled(Button)`
  font-size: var(--font-size-350);
  gap: 0;

  box-sizing: border-box;
  border: 3px solid transparent;

  html:not(.with-little-scrolled) &.theme-green:not(:active) {
    background: var(--nav-cta-background, var(--fill-brand));
    color: var(--nav-cta-ink, var(--fill-primary));
  }

  html.with-little-scrolled & {
    ${lightPill}
  }

  html.with-little-scrolled & svg {
    opacity: 0;
    display: none;
  }
`;

const RightNav: FC<RightNavProps> = ({ theme }) => {
  const { userState } = useContext(UserContext);
  const { userCart, openCart } = useContext(CartContext);

  // Also gates the bookmark fetch, so members outside the experiment don't
  // pay for a request they'll never use.
  const showJourney = useIsJourneyMember();
  const journeyHref = useJourneyDestination(showJourney);
  const { isOneTimePurchaser } = useIsOneTimePurchaser();
  const accountDropdownContent = getAccountDropdownContent({
    showJourney,
    journeyHref,
    isOneTimePurchaser,
  });
  const { featureFlags } = useFeatureFlags();
  const [currentTab, setCurrentTab] = useState<string>();
  const [selectedTab, setSelectedTab] = useState('');
  const router = useRouter();
  const [isClient, setIsClient] = useState(false);
  const navState = getNavState(userState, userCart);
  const { showGetStarted, showCart } = getNavCtas(navState, router.asPath);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isRolloutLoginEnabled = featureFlags.isRolloutLoginEnabled;

  useEffect(() => {
    setIsClient(true);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const resetTab = () => {
    setSelectedTab('');
    setCurrentTab('');
  };

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      resetTab();
    };
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  const handleClick = (id: string) => {
    if (id === currentTab) {
      setCurrentTab(undefined);
      setSelectedTab('');
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setCurrentTab(id);
    setSelectedTab(id);
    buttonEventHandler(id, 'Right Nav');
  };

  const handleMouseEnter = (id: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSelectedTab(id);
    setCurrentTab(id);
    trackEvent('Nav Menu Hover (desktop)', {
      menu: id,
    });
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCurrentTab('');
      setSelectedTab('');
    }, 300);
  };

  const handleMenuEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleCartButtonClick = () => {
    openCart();
    trackEvent('Button Clicked', {
      buttonName: 'Cart',
      location: 'Right Nav',
    });
  };

  if (!isClient) {
    return;
  }

  return (
    <SiteHeader>
      <SiteHeaderCtas>
        {userState?.email ? (
          <>
            <TabButton
              className={`theme-${theme} ${selectedTab === 'account' ? 'is-active' : ''}`}
              key='account'
              type='button'
              onClick={() => handleClick('account')}
              onMouseEnter={() => handleMouseEnter('account')}
              onMouseLeave={handleMouseLeave}
            >
              Account
              {selectedTab === 'account' ? (
                <TabPill
                  layoutId='tabPill'
                  id='tabPill'
                  transition={{
                    type: 'spring',
                    visualDuration: 0.2,
                    bounce: 0.2,
                  }}
                />
              ) : null}
            </TabButton>
            <TabButton
              className={`theme-${theme} ${userState?.experiments?.referral_nav ? 'with-label' : ''}`}
              onClick={() =>
                router.push('/account/refer?traffic_source=nav_earn')
              }
            >
              Refer
              {userState?.experiments?.referral_nav ? (
                <Referral25Label size='small'>$25</Referral25Label>
              ) : null}
            </TabButton>
          </>
        ) : (
          <LoginButton
            className={`theme-${theme}`}
            type='button'
            onClick={() => handleClick('login')}
          >
            Sign in
          </LoginButton>
        )}
        {showGetStarted ? (
          <CartButton
            url='/products'
            className={`theme-${theme}`}
            color={theme === 'white' ? 'snowwhite' : 'seedgreen'}
            size='medium'
            onClick={() => {
              trackEvent('Button Clicked', {
                buttonName: 'Get Started',
                location: 'Right Nav',
              });
            }}
          >
            Get Started
          </CartButton>
        ) : null}
        {showCart ? (
          <CartButton
            className={`theme-${theme}`}
            color={theme === 'white' ? 'snowwhite' : 'seedgreen'}
            size='medium'
            onClick={handleCartButtonClick}
          >
            Cart
            {!userCart.isEmpty() ? (
              <CartCount>&nbsp;{userCart.count()}</CartCount>
            ) : null}
          </CartButton>
        ) : null}
      </SiteHeaderCtas>

      <AnimatePresence key={currentTab} mode='wait'>
        {currentTab === 'login' && (
          <NavDropdownContent
            variants={tabVariants}
            initial='hidden'
            animate='visible'
            exit='hidden'
            alignment='right'
          >
            {isRolloutLoginEnabled ? (
              <LoginDropdownPercentRollout id='login' variants={listVariants} />
            ) : (
              <LoginDropdown id='login' variants={listVariants} />
            )}
          </NavDropdownContent>
        )}
        {currentTab === 'account' && (
          <NavDropdownContent
            variants={tabVariants}
            initial='hidden'
            animate='visible'
            exit='hidden'
            alignment='right'
            onMouseEnter={() => handleMenuEnter()}
            onMouseLeave={() => handleMouseLeave()}
          >
            <NavSubLists currentTab={accountDropdownContent} />
          </NavDropdownContent>
        )}
      </AnimatePresence>
    </SiteHeader>
  );
};

export default RightNav;
